import {
  AfterViewInit, Component, ElementRef, inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, ViewChild
} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {Chart} from 'chart.js/auto';
import {AiChatGrafico} from '../../../_models/ai-chat.model';
import {
  ColoriGrafico, configurazione, formattaValore, nomeFile, RigaAvanzamento, righeAvanzamento, Tessera, tessere, tsv,
  usaCanvas
} from './ai-chat-grafico-dati';

/** Parte di chart.js usata dal componente: tiene il modulo fuori dai generici di `Chart`. */
interface GraficoDisegnato {
  destroy(): void;
  toBase64Image(tipo?: string, qualita?: number): string;
}

const VARIABILI_SERIE = ['primary', 'warning', 'success', 'danger', 'tertiary', 'medium'];

/** Feature 160: grafico calcolato da AgentApi sotto una risposta della chat (chart.js o markup Ionic). */
@Component({
  selector: 'app-ai-chat-grafico',
  templateUrl: './ai-chat-grafico.component.html',
  styleUrls: ['./ai-chat-grafico.component.scss'],
  standalone: false
})
export class AiChatGraficoComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({required: true})
  set grafico(grafico: AiChatGrafico) {
    this._grafico = grafico;
    this.canvas = usaCanvas(grafico);
    this.tessere = grafico.tipo === 'kpi' ? tessere(grafico) : [];
    this.righe = grafico.tipo === 'avanzamento' ? righeAvanzamento(grafico) : [];
  }

  get grafico(): AiChatGrafico {
    return this._grafico;
  }

  @Input() grande = false;
  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;

  chart: GraficoDisegnato | null = null;
  copiato = false;
  // Calcolati una volta per grafico (nel setter, che vale anche per i componentProps del modale), non a ogni
  // change detection: oggetti nuovi a ogni giro facevano ricreare a *ngFor le tessere, e con un chart.js nella
  // stessa conversazione il suo ResizeObserver rilanciava la change detection all'infinito (tab bloccato).
  canvas = false;
  tessere: Tessera[] = [];
  righe: RigaAvanzamento[] = [];
  readonly formattaValore = formattaValore;
  private readonly modalController = inject(ModalController);
  private readonly zone = inject(NgZone);
  private viewInizializzata = false;
  private _grafico!: AiChatGrafico;

  ngAfterViewInit() {
    this.viewInizializzata = true;
    this.creaChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.viewInizializzata && changes['grafico']) setTimeout(() => this.creaChart());
  }

  ngOnDestroy() {
    this.distruggi();
  }

  async espandi() {
    const modale = await this.modalController.create({
      component: AiChatGraficoComponent, componentProps: {grafico: this.grafico, grande: true},
      cssClass: 'ai-chat-grafico-modale'
    });
    await modale.present();
  }

  chiudi() {
    return this.modalController.dismiss();
  }

  scaricaPng() {
    if (!this.chart) return;
    const link = document.createElement('a');
    link.href = this.chart.toBase64Image('image/png', 1);
    link.download = nomeFile(this.grafico);
    link.click();
  }

  async copiaDati() {
    try {
      await navigator.clipboard.writeText(tsv(this.grafico));
      this.copiato = true;
      setTimeout(() => this.copiato = false, 1500);
    } catch {
      this.copiato = false;
    }
  }

  private creaChart() {
    this.distruggi();
    if (!this.canvas || !this.canvasRef) return;
    // Fuori dalla zona Angular: ridimensionamenti e animazioni di chart.js non rilanciano la change detection.
    const canvas = this.canvasRef.nativeElement;
    this.chart = this.zone.runOutsideAngular(() => new Chart(canvas, configurazione(this.grafico, colori()) as never));
  }

  private distruggi() {
    const chart = this.chart;
    if (chart) this.zone.runOutsideAngular(() => chart.destroy());
    this.chart = null;
  }
}

// Colori dal tema Ionic al momento della creazione: seguono il tema chiaro o scuro.
function colori(): ColoriGrafico {
  const stile = getComputedStyle(document.documentElement);
  const variabile = (nome: string, riserva: string) => stile.getPropertyValue(nome).trim() || riserva;
  return {
    serie: VARIABILI_SERIE.map(v => variabile(`--ion-color-${v}`, '#3880ff')),
    testo: variabile('--ion-text-color', '#222428'),
    griglia: variabile('--ion-color-step-150', 'rgba(128,128,128,0.2)')
  };
}
